// src/pages/SalesPages/ReceiptModal.jsx
import React, { useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { FiPrinter, FiMail } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import styles from "./ReceiptModal.module.css";

const logReceipt = (action, receipt) => {
  console.log(`[${new Date().toLocaleString()}] ${action}`, receipt);
};

const fmt = (n) => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "0.00";
  return v.toFixed(2);
};

const ReceiptModal = ({ open, onClose, receipt }) => {
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showWhatsAppInput, setShowWhatsAppInput] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+7");

  const totalSum = useMemo(() => {
    return (
      receipt?.items?.reduce(
        (sum, item) => sum + (item.price ?? 0) * (item.qty ?? 0),
        0
      ) ?? 0
    );
  }, [receipt]);

  const finalTotal = Number(receipt?.total ?? totalSum ?? 0);

  const paid = Number(receipt?.paid ?? 0);
  const change = Math.max(0, Number(receipt?.change ?? 0));
  const showChangeRow = change > 0;

  const shiftNumber =
    receipt?.shiftNumber ?? receipt?.shift_id ?? receipt?.shiftId ?? "—";
  const cashierName = receipt?.employee ?? "—";

  const handleEmailSend = () => {
    if (!email) return alert("Введите email!");
    alert(`Чек отправлен на ${email}`);
    setEmail("");
    setShowEmailInput(false);
  };

  const handleWhatsAppSend = () => {
    if (!phone || phone.length < 10) return alert("Введите корректный номер!");

    const msg = [
      `Чек №${receipt?.checkNumber || "-"}`,
      `Итого: ${fmt(finalTotal)} ₸`,
      `Оплачено: ${fmt(paid || finalTotal)} ₸`,
      showChangeRow ? `Сдача: ${fmt(change)} ₸` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const url = `https://wa.me/${phone.replace(/\D/g, "")}?text=${encodeURIComponent(
      msg
    )}`;
    window.open(url, "_blank");
  };

  const handlePrint = () => {
    logReceipt("Печать по нажатию иконки", receipt);
    window.print();
  };

  if (!open || !receipt) return null;

  const paymentLabel = receipt?.paymentType || "Наличные";

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} receipt-print`}>
        {/* TOP */}
        <div className={styles.topBar}>
          <div className={styles.topTitle}>Просмотр чека</div>
          <button className={styles.topClose} onClick={onClose} aria-label="close">
            ×
          </button>
        </div>

        {/* SCROLL AREA */}
        <div className={styles.scrollArea}>
          {/* RECEIPT PAPER */}
          <div className={styles.paperWrap}>
            <div className={styles.paper}>
              {/* HEADER CENTER */}
              <div className={styles.centerBlock}>
                <div className={styles.centerTitle}>{receipt.companyName || "ТЕСТ"}</div>
                <div className={styles.centerSub}>
                  БИН/ИИН {receipt.bin || "--------"}
                </div>
              </div>

              <div className={styles.hr} />

              <div className={styles.sectionTitle}>Продажа</div>

              <div className={styles.hrThin} />

              {/* two boxes */}
              <div className={styles.twoCols}>
                <div className={styles.box}>
                  <div className={styles.boxLabel}>Порядковый номер чека №,</div>
                  <div className={styles.boxLabel}>Время:</div>

                  <div className={styles.boxValue}>№{receipt.checkNumber || "—"}</div>
                  <div className={styles.boxSmall}>
                    {receipt.time || new Date().toLocaleString()}
                  </div>
                </div>

                <div className={styles.box}>
                  <div className={styles.boxLabel}>Номер смены:</div>
                  <div className={styles.boxLabel}>кассир</div>

                  <div className={styles.boxValue}>№{shiftNumber}</div>
                  <div className={styles.boxSmall}>{cashierName}</div>
                </div>
              </div>

              <div className={styles.hr} />

              <div className={styles.sectionTitleSmall}>СПИСОК ТОВАРОВ / УСЛУГ</div>

              <div className={styles.hrThin} />

              <div className={styles.items}>
                {(receipt.items || []).map((item, idx) => {
                  const price = Number(item.price ?? 0);
                  const qty = Number(item.qty ?? 0);
                  const line = price * qty;

                  return (
                    <div key={idx} className={styles.item}>
                      <div className={styles.itemName}>
                        <span className={styles.itemIndex}>{idx + 1}.</span>{" "}
                        <span className={styles.itemNameText}>{item.name || "—"}</span>
                      </div>

                      <div className={styles.itemRow}>
                        <div className={styles.itemLeft}>
                          {qty.toFixed(3)} × {fmt(price)} ₸
                        </div>
                        <div className={styles.itemRight}>{fmt(line)} ₸</div>
                      </div>

                      <div className={styles.itemRowMuted}>
                        <div className={styles.itemLeft}>Стоимость</div>
                        <div className={styles.itemRight}>{fmt(line)} ₸</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.hr} />

              <div className={styles.totals}>
                <div className={styles.totalLineMain}>
                  <div className={styles.totalLabel}>ИТОГО:</div>
                  <div className={styles.totalValue}>{fmt(finalTotal)} ₸</div>
                </div>

                <div className={styles.totalLine}>
                  <div className={styles.totalLabel2}>{paymentLabel}:</div>
                  <div className={styles.totalValue2}>{fmt(paid || finalTotal)} ₸</div>
                </div>

                {showChangeRow && (
                  <div className={styles.totalLine}>
                    <div className={styles.totalLabel2}>Сдача:</div>
                    <div className={styles.totalValue2}>{fmt(change)} ₸</div>
                  </div>
                )}
              </div>

              <div className={styles.qrWrap}>
                <QRCodeCanvas
                  value={
                    receipt.qrCode ||
                    `Чек №${receipt.checkNumber || "-"} Сумма: ${fmt(finalTotal)} ₸`
                  }
                  size={180}
                  includeMargin
                />
              </div>

              <div className={styles.hrThin} />

              <div className={styles.address}>Адрес: {receipt.address || "---------"}</div>
            </div>
          </div>
        </div>

        {/* bottom icons */}
        <div className={styles.actionBar}>
          <button title="Печать" onClick={handlePrint} className={styles.actionBtn}>
            <FiPrinter size={26} />
          </button>

          <button
            title="Отправить по Email"
            onClick={() => {
              setShowEmailInput((p) => !p);
              setShowWhatsAppInput(false);
            }}
            className={styles.actionBtn}
          >
            <FiMail size={26} />
          </button>

          <button
            title="Поделиться в WhatsApp"
            onClick={() => {
              setShowWhatsAppInput((p) => !p);
              setShowEmailInput(false);
            }}
            className={styles.actionBtn}
          >
            <FaWhatsapp size={28} />
          </button>
        </div>

        {/* inputs */}
        {showEmailInput && (
          <div className={styles.inputContainer}>
            <input
              type="email"
              placeholder="Введите email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button onClick={handleEmailSend}>Отправить</button>
          </div>
        )}

        {showWhatsAppInput && (
          <div className={styles.inputContainer}>
            <input
              type="tel"
              placeholder="+7XXXXXXXXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <button onClick={handleWhatsAppSend}>Отправить</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptModal;
